import { Component, Inject, Vue } from "vue-property-decorator";
import firebase from "firebase/app";
import moment from "moment";
import { firebaseDB } from "@/plugins/firebase";
import { Chat } from "../../models/Chat.Model";
import { FORM_MESSAGES, LOCAL_STORAGE } from "@/shared/constants/FormConstants";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import { GENERAL_CONSTANTS } from "@/shared/constants/GeneralConstants";
import DynamicSelect from "@/shared/components/DynamicSelect/DynamicSelect.vue";
import { UserService } from "@/modules/User/services/UserService";
import { Http } from "@status/codes";
import { UserStatus } from "@/modules/User/models/UserStatus";
import { DynamicSelectConfig } from "@/shared/components/DynamicSelect/model/DynamicSelectConfig";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { USER_ACCESS } from "@/modules/User/constants/UserConstants";
import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import { Dependency } from "@/modules/Dependencies/models/Dependency";
import {
  CHAT_MESSAGES,
  CHATS_FIELDS,
  FIREBASE_EVENTS,
  LOCAL_CHAT_TYPE
} from "../../constants/LocalChatWindow.constants";

// TODO: Refactor y agregar constantes

@Component({
  name: "ListChats",
  components: { DynamicSelect, Snackbar }
})
export default class ListChats extends Vue {
  @Inject() userService!: UserService;
  private clientId!: string;
  private userId!: string;
  private usersRef!: firebase.firestore.CollectionReference;
  private chatsRef!: firebase.firestore.CollectionReference;

  textFieldClass = STYLE_CLASSES.TEXT_FIELD;
  primaryBtnRoundClass = STYLE_CLASSES.PRIMARY_ROUND_BTN;
  currentIcon = GENERAL_CONSTANTS.ICONS.ADD;
  searchFilter = "";
  filteredChats: Chat[] = [];
  directChats: Chat[] = [];
  groupChats: Chat[] = [];
  searchText = "";
  userLastRef = "";
  listUsers: UserStatus[] = [];
  selectInputs: DynamicSelectData[] = [];
  userDependencies: string[] = [];

  dynamicSelectConfig: DynamicSelectConfig = {
    multiple: false,
    default: [],
    listAll: [],
    chips: false,
    filled: false,
    notFound: false,
    persistenthint: true,
    loadingData: false,
    messageLabel: "Buscar usuario",
    attach: false
  };

  mounted() {
    this.initializeUserData();
    if (this.clientId) {
      this.initializeFirebaseReferences();
      this.loadChats();
    }
  }

  private initializeUserData() {
    this.userId = localStorage.getItem(LOCAL_STORAGE.UID) || "";
    this.clientId = localStorage.getItem(LOCAL_STORAGE.CLIENT_ID) || "";
  }

  private initializeFirebaseReferences() {
    const clientRef = firebaseDB.collection("clients").doc(this.clientId);
    this.usersRef = clientRef.collection("users");
    this.chatsRef = clientRef.collection("chats");
  }

  private loadChats() {
    this.getDirectChats();
    this.getGroupChats();
  }

  async onChange() {
    const user = this.selectInputs[0];
    const existingChat = this.findExistingChat(user.id);

    if (existingChat) {
      this.goToChat(existingChat);
    } else {
      const newChat = await this.createNewChat(user);
      this.goToChat(newChat);
    }
  }

  private findExistingChat(userId: string): Chat | undefined {
    return this.directChats.find(chat => chat.members.includes(userId));
  }

  private async createNewChat(user: DynamicSelectData): Promise<Chat> {
    const newChat: Partial<Chat> = {
      dateLastMessage: Date.now(),
      lastMessage: "",
      members: [this.userId, user.id],
      seen: true,
      seenBy: [this.userId, user.id],
      type: LOCAL_CHAT_TYPE.DIRECT
    };

    const createdChat = await this.chatsRef.add(newChat);
    return { ...newChat, id: createdChat.id, name: user.name } as Chat;
  }

  async findUsers(name: string) {
    if (!name) return;

    this.dynamicSelectConfig.loadingData = true;
    try {
      const responseData = await this.userService.getUserByFilters({
        userState: "Activo",
        externalUser: false,
        name
      });

      this.handleUserSearchResponse(responseData);
    } catch (error) {
      this.handleUserSearchError(error);
    } finally {
      this.dynamicSelectConfig.loadingData = false;
    }
  }

  private handleUserSearchResponse(responseData: any) {
    if (responseData.code === Http.Found && responseData.data) {
      this.listUsers = [...this.listUsers, ...responseData.data.userArray];
      this.userLastRef = responseData.data.lastReference;
      this.updateDynamicSelectList();
    } else {
      this.listUsers = [];
      Snackbar.popInfo(responseData.message);
    }
  }

  private updateDynamicSelectList() {
    this.dynamicSelectConfig.listAll = this.listUsers.map(user => ({
      id: user.id as string,
      name: user.name as string
    }));
  }

  private handleUserSearchError(error: unknown) {
    const errorMessage =
      (error as Error).message === GENERAL_CONSTANTS.FORBIDDEN
        ? `${FORM_MESSAGES.FORBIDDEN_REQUEST}${USER_ACCESS.GET}`
        : FORM_MESSAGES.REQUEST_FAILED;

    Snackbar.popError(errorMessage);
  }

  getDirectChats() {
    try {
      this.directChats = [];
      this.chatsRef
        .where(CHATS_FIELDS.MEMBERS, "array-contains", this.userId)
        .orderBy(CHATS_FIELDS.DATE_LAST_MESSAGE, "asc")
        .onSnapshot(snapshot =>
          this.handleChatSnapshot(snapshot, this.directChats)
        );
    } catch (error) {
      console.error(CHAT_MESSAGES.ERROR_DIRECT_LOAD, error);
    }
  }

  async getGroupChats() {
    try {
      await this.getUserInfo();
      this.groupChats = [];
      this.chatsRef
        .where(CHATS_FIELDS.DEPENDENCY, "in", this.userDependencies)
        .orderBy(CHATS_FIELDS.DATE_LAST_MESSAGE, "asc")
        .onSnapshot(snapshot =>
          this.handleChatSnapshot(snapshot, this.groupChats)
        );
    } catch (error) {
      console.error(CHAT_MESSAGES.ERROR_GROUP_LOAD, error);
    }
  }

  private handleChatSnapshot(
    snapshot: firebase.firestore.QuerySnapshot,
    chatList: Chat[]
  ) {
    snapshot.docChanges().forEach(async change => {
      const chat = await this.processChatChange(change);
      if (chat) this.updateChatList(chat, chatList, change.type);
    });
  }

  private async processChatChange(
    change: firebase.firestore.DocumentChange
  ): Promise<Chat | null> {
    const chat = change.doc.data() as Chat;
    chat.id = change.doc.id;
    chat.seen = chat.seenBy.includes(this.userId) || false;
    chat.dateText = moment(chat.dateLastMessage)
      .locale("es")
      .fromNow();

    if (chat.members) {
      chat.name = await this.getChatName(chat.members);
    }

    return chat;
  }

  private async getChatName(members: string[]): Promise<string> {
    const otherMembers = members.filter(member => member !== this.userId);
    const names = await Promise.all(
      otherMembers.map(async memberId => {
        const user = (await this.usersRef.doc(memberId).get()).data();
        return user?.visualName || "";
      })
    );
    return names.join(", ");
  }

  private updateChatList(chat: Chat, chatList: Chat[], changeType: string) {
    if (
      [FIREBASE_EVENTS.ADDED, FIREBASE_EVENTS.MODIFIED].includes(changeType)
    ) {
      const existingIndex = chatList.findIndex(item => item.id === chat.id);

      if (existingIndex >= 0) {
        if (chatList[existingIndex].dateLastMessage !== chat.dateLastMessage) {
          chatList.splice(existingIndex, 1);
        } else {
          chatList[existingIndex] = chat;
          this.$forceUpdate();
          return;
        }
      }
      chatList.unshift(chat);
    }
  }

  goToChat(chat: Chat) {
    this.$emit("goChat", chat);
  }

  async getUserInfo() {
    try {
      const responseData = await this.userService.getUserById(this.userId);
      if (responseData.code === Http.Found && responseData.data) {
        this.userDependencies = (responseData.data.userData
          .dependencies as Dependency[]).map(
          dependency => dependency.id as string
        );
      }
    } catch (error) {
      console.error(CHAT_MESSAGES.ERROR_USER_LOAD, error);
    }
  }
}
